import React from 'react';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import routes from 'constants/routes';
import { getDetailOfDealOrCaseInCRM } from 'features/common/helpers';
import { DealSaleOrderConflict } from '../helpers/dealSaleOrderConflict';

interface Props {
	conflict: DealSaleOrderConflict;
	onBackToQuotations?: () => void;
}

export const WizardDealSaleOrderConflict = ({ conflict, onBackToQuotations }: Props): JSX.Element => {
	const { crmTicket, quotationId, saleOrder } = conflict;
	const orderUrl = routes.salesOrder.edit.replace(':id', saleOrder.id);
	const quotationUrl = routes.quotations.edit.replace(':id', String(quotationId));
	const caseUrl = getDetailOfDealOrCaseInCRM(crmTicket, saleOrder.isBitrixDeal);

	return (
		<Box>
			<Typography variant="h5" fontWeight={700} gutterBottom>
				No se puede crear otra orden para este caso
			</Typography>

			<Alert severity="warning" sx={{ mb: 2 }}>
				<Typography variant="body2" component="div" gutterBottom>
					La cotización <strong>#{quotationId}</strong> está asociada al caso de venta{' '}
					<Link href={caseUrl} target="_blank" rel="noopener noreferrer" fontWeight={600}>
						{crmTicket}
					</Link>
					, y ese caso <strong>ya tiene una orden de venta</strong>. Solo puede existir una orden por caso.
				</Typography>
				<Typography variant="body2" component="div" sx={{ mt: 1 }}>
					Orden existente:{' '}
					<Link component={RouterLink} to={orderUrl} fontWeight={600}>
						{saleOrder.id}
					</Link>
					{saleOrder.quotationId ? <> (cotización {saleOrder.quotationId} en la orden)</> : null}
				</Typography>
			</Alert>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Si necesitás generar otra orden a partir de la misma cotización, duplicá la cotización con un{' '}
				<strong>nuevo caso de venta</strong> y volvé a usar &quot;Crear orden&quot; desde la copia.
			</Typography>

			<Box display="flex" flexWrap="wrap" gap={2}>
				<Button variant="contained" component={RouterLink} to={orderUrl}>
					Abrir orden de venta existente
				</Button>
				<Button variant="outlined" component={RouterLink} to={quotationUrl}>
					Ver cotización
				</Button>
				{onBackToQuotations && (
					<Button variant="text" onClick={onBackToQuotations}>
						Volver al listado de cotizaciones
					</Button>
				)}
			</Box>
		</Box>
	);
};
